// @ts-expect-error: No declarations for @ember/owner
import { type Owner, setOwner } from '@ember/owner';
import { type Registry as Services, service } from '@ember/service';
import type ReferenceItem from 'lumen/utils/datamodels/total-revenue/reference-item';
import safeDivide from 'lumen/utils/numeric/safe-divide';

/**
 * POJO-copy for the total-revenue/item model
 * Can be used to represent an item we don't want to persist in the store
 */
export default class Item {
  @service('total-revenue')
  declare totalRevenueService: Services['total-revenue'];

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get formattedNonRoomPremium() {
    return this.totalRevenueService.formatValue(this.nonRoomPremium);
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get formattedTotalNonRoomRevenue() {
    return this.totalRevenueService.formatValue(this.totalNonRoomRevenueOrPAR);
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get formattedTotalNonRoomRevenuePerRoomNight() {
    return this.totalRevenueService.formatValue(
      this.totalNonRoomRevenuePerRoomNight,
    );
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get formattedTotalRevenue() {
    return this.totalRevenueService.formatValue(this.totalRevenueOrPAR);
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get formattedTotalRevenuePerRoomNight() {
    return this.totalRevenueService.formatValue(this.totalRevenuePerRoomNight);
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get formattedTotalRoomRevenue() {
    return this.totalRevenueService.formatValue(this.totalRoomRevenueOrPAR);
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get formattedTotalRoomRevenuePerRoomNight() {
    return this.totalRevenueService.formatValue(
      this.totalRoomRevenuePerRoomNight,
    );
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get nonRoomPremium() {
    return this.totalRevenueService.calculateNonRoomPremium(
      this.totalNonRoomRevenue,
      this.totalRoomRevenue,
    );
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get sortedReferenceValues() {
    return [...this.referenceValues].sort(
      (a, b) => a.referenceFromDate.valueOf() - b.referenceFromDate.valueOf(),
    );
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get totalNonRoomRevenueOrPAR() {
    return this.totalRevenueService.valueOrPAR(
      this.totalNonRoomRevenue,
      this.capacity,
    );
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get totalNonRoomRevenuePerRoomNight() {
    return safeDivide(this.totalNonRoomRevenue, this.nrOfRooms ?? 0);
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get totalRevenueOrPAR() {
    return this.totalRevenueService.valueOrPAR(
      this.totalRevenue,
      this.capacity,
    );
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get totalRevenuePerRoomNight() {
    return safeDivide(this.totalRevenue, this.nrOfRooms ?? 0);
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get totalRoomRevenueOrPAR() {
    return this.totalRevenueService.valueOrPAR(
      this.totalRoomRevenue,
      this.capacity,
    );
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get totalRoomRevenuePerRoomNight() {
    return safeDivide(this.totalRoomRevenue, this.nrOfRooms ?? 0);
  }

  // eslint-disable-next-line max-params
  constructor(
    public owner: Owner,
    public totalRevenue: number,
    public totalRoomRevenue: number,
    public totalNonRoomRevenue: number,
    public capacity: number | undefined,
    public nrOfRooms: number | undefined,
    public referenceValues: ReferenceItem[],
  ) {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
    setOwner(this, owner);

    referenceValues.forEach((referenceValue) => {
      referenceValue.item = this;
    });
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  static create(
    owner: Owner,
    data: {
      capacity?: number;
      nrOfRooms?: number;
      referenceValues: ReferenceItem[];
      totalNonRoomRevenue: number;
      totalRevenue: number;
      totalRoomRevenue: number;
    },
  ) {
    return new Item(
      owner,
      data.totalRevenue,
      data.totalRoomRevenue,
      data.totalNonRoomRevenue,
      data.capacity,
      data.nrOfRooms,
      data.referenceValues,
    );
  }
}
